import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form';
import ButtonToggler from './ButtonToggler';
import Counter from './Counter';
import EventTypeTabs from './EventTypeTabs';
import { EventItem, EventsData, QuickCreateFormTypes, eventsSchema } from '../types';

const Events: React.FC<QuickCreateFormTypes> = ({ formData, setFormData, nextStep, prevStep }) => {
  const defaultValues: EventsData = {
    maxEvents: formData?.events?.maxEvents || 0,
    events: formData?.events?.events || [],
  };

  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<EventsData>({
    resolver: zodResolver(eventsSchema),
    defaultValues,
  });

  const maxEvents = useWatch({ control, name: 'maxEvents' });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'events',
  });

  useEffect(() => {
    const eventCount = fields.length;
    if (maxEvents > eventCount) {
      // Append events until the length matches maxEvents
      for (let i = eventCount; i < maxEvents; i++) {
        append({ doubles: '', singles: '', activeTab: 0, eventId: i + 1 });
      }
    } else if (maxEvents < eventCount) {
      // Remove events until the length matches maxEvents
      for (let i = eventCount; i > maxEvents; i--) {
        remove(i - 1);
      }
    }
  }, [maxEvents, fields, append, remove]);

  const onSubmit: SubmitHandler<EventsData> = (data) => {
    console.log('onSubmit called', data);

    if (setFormData && nextStep) {
      setFormData((prevData) => ({ ...prevData, events: data }));
      if (formData?.events) {
        nextStep(); // This should navigate to the next step/component
      }
    }
  };

  const updateEventsTab = (idx: number, val: number, field: EventItem) => {
    update(idx, { ...field, activeTab: val });
  };

  return (
    <>
      <div className="mx-auto w-full max-w-[23.5rem]">
        <h5 className="typography-product-heading mb-6 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          Events
        </h5>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <div>
            <Counter
              label="How many events do you want?"
              errors={errors}
              inputProps={{
                type: 'number',
                onWheel: (e) => {
                  e.preventDefault();
                  e.currentTarget.blur();
                },
                min: 1,
              }}
              name="maxEvents"
              minValue={1}
              register={register}
              setValue={setValue}
              getValues={getValues}
              control={control}
            />
            {errors.maxEvents && (
              <p className="mt-2 text-sm text-color-text-brand dark:text-color-text-brand">
                {errors.maxEvents.message}
              </p>
            )}
          </div>

          {fields.map((event, index) => (
            <div key={index}>
              <label
                htmlFor={`event-${index + 1}`}
                className="typography-product-subheading mb-3 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
              >
                Event {index + 1}
              </label>
              <EventTypeTabs
                field={event}
                index={index}
                control={control}
                errors={errors}
                onChange={updateEventsTab}
                register={register}
                maxEvents={fields.length}
              />
            </div>
          ))}
        </form>
      </div>
      <ButtonToggler
        type="button"
        isLoading={false}
        prevStep={prevStep}
        nextStep={() => {
          if (!isValid) {
            handleSubmit(onSubmit)();
          } else if (formData?.events && nextStep) {
            handleSubmit(onSubmit)();
          }
        }}
        nextTitle="Next"
      />
    </>
  );
};

export default Events;
